import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import ConfirmDialog from "../../components/admin/ConfirmDialog";
import { Plus, Pencil, Trash2, Upload } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Card } from "../../components/ui/card";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "../../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import PageHeader from "../../components/admin/PageHeader";
import InfoBanner from "../../components/admin/InfoBanner";
import FilterBar from "../../components/admin/FilterBar";

const BASE_URI = process.env.REACT_APP_API_BASE_URI;
const emptyForm = { title: "", date_day: "", date_month_year: "", description: "", is_new: false, images: [], imagePreviews: [] };

const compressImage = (file) => new Promise((resolve) => {
  const reader = new FileReader();
  reader.onload = (e) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      let [w, h] = [img.width, img.height];
      if (w > 800) { h *= 800 / w; w = 800; }
      if (h > 600) { w *= 600 / h; h = 600; }
      canvas.width = w; canvas.height = h;
      canvas.getContext("2d").drawImage(img, 0, 0, w, h);
      canvas.toBlob((blob) => resolve(new File([blob], file.name, { type: "image/jpeg", lastModified: Date.now() })), "image/jpeg", 0.9);
    };
    img.src = e.target.result;
  };
  reader.readAsDataURL(file);
});

const EventCRUD = () => {
  const [events, setEvents] = useState([]);
  const [search, setSearch] = useState("");
  const [form, setForm] = useState(emptyForm);
  const [editEvent, setEditEvent] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [error, setError] = useState("");
  const [pagination, setPagination] = useState({ page: 1, page_size: 10, total: 0, total_pages: 1 });

  const getAuthHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchEvents(); }, []);

  const fetchEvents = async (page = 1) => {
    try {
      setLoading(true);
      const res = await axios.get(`${BASE_URI}/entities?category=event&page=${page}&limit=${pagination.page_size}`, getAuthHeaders());
      if (res.data?.data) {
        setEvents(res.data.data.map((i) => ({ id: i.id, title: i.data.title || "", date_day: i.data.date_day || "", date_month_year: i.data.date_month_year || "", description: i.data.description || "", is_new: i.data.is_new || false, images: i.data.images || [] })));
        setPagination({ page: res.data.page, page_size: res.data.limit, total: res.data.total, total_pages: Math.ceil(res.data.total / res.data.limit) });
      }
      setError("");
    } catch { setError("Failed to fetch events"); }
    finally { setLoading(false); }
  };

  const openAdd = () => { setForm(emptyForm); setEditEvent(null); setDialogOpen(true); };
  const openEdit = (ev) => { setForm({ title: ev.title, date_day: ev.date_day, date_month_year: ev.date_month_year, description: ev.description, is_new: ev.is_new, images: [], imagePreviews: ev.images || [] }); setEditEvent(ev); setDialogOpen(true); };

  const handleImages = async (e) => {
    const files = Array.from(e.target.files);
    const compressed = await Promise.all(files.filter((f) => f.size <= 5 * 1024 * 1024).map(compressImage));
    const previews = compressed.map((f) => URL.createObjectURL(f));
    setForm((f) => ({ ...f, images: [...f.images, ...compressed], imagePreviews: [...f.imagePreviews, ...previews] }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { toast.warning("Please enter an event title"); return; }
    try {
      setLoading(true);
      const fd = new FormData();
      fd.append("title", form.title); fd.append("date_day", form.date_day);
      fd.append("date_month_year", form.date_month_year); fd.append("description", form.description);
      fd.append("is_new", form.is_new);
      if (editEvent) fd.append("appendImages", "true");
      form.images.forEach((img) => fd.append("images", img));
      const hdrs = { headers: { "Content-Type": "multipart/form-data", Authorization: `Bearer ${localStorage.getItem("token")}` } };
      if (editEvent) {
        await axios.put(`${BASE_URI}/entities/${editEvent.id}?category=event`, fd, hdrs);
        toast.success("Event updated!");
      } else {
        await axios.post(`${BASE_URI}/entities?category=event`, fd, hdrs);
        toast.success("Event created!");
      }
      setDialogOpen(false); fetchEvents(pagination.page);
    } catch { toast.error("Save failed"); }
    finally { setLoading(false); }
  };

  const deleteEvent = async () => {
    try {
      setDeleteLoading(true);
      await axios.delete(`${BASE_URI}/entities/${deleteId}?category=event`, getAuthHeaders());
      toast.success("Deleted successfully!");
      setDeleteId(null);
      fetchEvents(events.length === 1 && pagination.page > 1 ? pagination.page - 1 : pagination.page);
    } catch { toast.error("Failed to delete."); }
    finally { setDeleteLoading(false); }
  };

  const filtered = events.filter(e => e.title.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="space-y-6">
      <PageHeader
        title="Events Management"
        description="Manage events and programmes displayed on the website"
        action={<Button onClick={openAdd}><Plus className="h-4 w-4" /> Add Event</Button>}
      />
      <InfoBanner
        title="Events Management"
        description="Create and update events with dates, descriptions, and multiple images."
        capabilities={["Multi-image upload", "Mark events as new", "Date with day & month/year"]}
      />
      <FilterBar search={search} onSearch={setSearch} placeholder="Search by title…" count={filtered.length} />
      {error && <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}

      <Card className="overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[110px]">Images</TableHead>
              <TableHead>Title</TableHead>
              <TableHead className="w-[100px]">Date</TableHead>
              <TableHead className="w-[70px]">Status</TableHead>
              <TableHead>Description</TableHead>
              <TableHead className="w-[120px] text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={6} className="text-center py-12 text-gray-400">
                <div className="flex items-center justify-center gap-2"><div className="h-4 w-4 rounded-full border-2 border-blue-400 border-t-transparent animate-spin" />Loading…</div>
              </TableCell></TableRow>
            ) : filtered.length === 0 ? (
              <TableRow><TableCell colSpan={6} className="text-center py-12 text-gray-400">No events yet.</TableCell></TableRow>
            ) : filtered.map((ev) => (
              <TableRow key={ev.id}>
                <TableCell>
                  <div className="flex gap-1 flex-wrap">
                    {(ev.images || []).slice(0, 2).map((url, i) => (
                      <img key={i} src={url} alt="" className="h-10 w-14 rounded object-cover border border-gray-100" onError={(e) => { e.target.style.display = "none"; }} />
                    ))}
                    {(ev.images || []).length > 2 && <span className="text-xs text-gray-400 self-center">+{ev.images.length - 2}</span>}
                  </div>
                </TableCell>
                <TableCell className="font-medium text-gray-900"><p className="line-clamp-2 max-w-[180px]">{ev.title}</p></TableCell>
                <TableCell>
                  <div className="font-bold text-gray-900">{ev.date_day}</div>
                  <div className="text-xs text-gray-500">{ev.date_month_year}</div>
                </TableCell>
                <TableCell><Badge variant={ev.is_new ? "new" : "secondary"}>{ev.is_new ? "New" : "Regular"}</Badge></TableCell>
                <TableCell className="text-gray-600 text-sm max-w-[200px]"><p className="line-clamp-2">{ev.description}</p></TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-2">
                    <Button size="sm" variant="outline" onClick={() => openEdit(ev)}><Pencil className="h-3.5 w-3.5" /></Button>
                    <Button size="sm" variant="destructive" onClick={() => setDeleteId(ev.id)}><Trash2 className="h-3.5 w-3.5" /></Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {pagination.total_pages > 1 && (
          <div className="flex items-center justify-center gap-1 p-4 border-t border-gray-100">
            <Button size="sm" variant="outline" onClick={() => fetchEvents(pagination.page - 1)} disabled={pagination.page === 1 || loading}>Previous</Button>
            {Array.from({ length: pagination.total_pages }, (_, i) => i + 1).map((p) => (
              <Button key={p} size="sm" variant={p === pagination.page ? "default" : "outline"} onClick={() => fetchEvents(p)} disabled={loading}>{p}</Button>
            ))}
            <Button size="sm" variant="outline" onClick={() => fetchEvents(pagination.page + 1)} disabled={pagination.page === pagination.total_pages || loading}>Next</Button>
          </div>
        )}
      </Card>

      <ConfirmDialog
        open={!!deleteId}
        title="Delete event?"
        description="This action cannot be undone."
        onConfirm={deleteEvent}
        onCancel={() => setDeleteId(null)}
        loading={deleteLoading}
      />

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{editEvent ? "Edit Event" : "Add New Event"}</DialogTitle></DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 px-6 py-4">
            <div className="space-y-1.5">
              <Label>Event Title <span className="text-red-500">*</span></Label>
              <Input placeholder="Event title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Day</Label>
                <Input placeholder="e.g. 15" value={form.date_day} onChange={(e) => setForm({ ...form, date_day: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label>Month &amp; Year</Label>
                <Input placeholder="e.g. August 2025" value={form.date_month_year} onChange={(e) => setForm({ ...form, date_month_year: e.target.value })} />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Description</Label>
              <Textarea placeholder="Event description…" rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </div>
            <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
              <input type="checkbox" checked={form.is_new} onChange={(e) => setForm({ ...form, is_new: e.target.checked })} className="rounded border-gray-300" />
              Mark as New Event
            </label>
            <div className="space-y-1.5">
              <Label>Event Images</Label>
              <label className="flex items-center gap-2 cursor-pointer rounded-lg border border-dashed border-gray-300 px-4 py-2.5 text-sm text-gray-600 hover:bg-gray-50 transition-colors w-fit">
                <Upload className="h-4 w-4" /> Upload images
                <input type="file" accept="image/*" multiple onChange={handleImages} className="hidden" />
              </label>
              {form.imagePreviews.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {form.imagePreviews.map((url, i) => (
                    <img key={i} src={url} alt="" className="h-14 w-20 rounded-lg object-cover border border-gray-200" />
                  ))}
                </div>
              )}
            </div>
          </form>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSubmit} disabled={loading}>{loading ? "Saving…" : editEvent ? "Update" : "Create"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EventCRUD;
